import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Trash2, Eye, Settings } from 'lucide-react';
import { GympeChatHeader, DocumentPreviewCard } from '../../components/notifications/WhatsAppPreview';

interface TemplateGym {
    gymId: string;
    gymName: string;
    tenantId: string;
    isEnabled: boolean;
    templateId: string;
}

interface Template {
    templateName: string;
    triggerEvent: string;
    title: string;
    content: string;
    language: string;
    parameterCount: number;
    sampleParams: Record<string, string>;
    type: string;
    headerType?: string | null;
    gyms: TemplateGym[];
}

interface Gym {
    id: string;
    name: string;
    displayName: string;
    ownerId: string;
}

interface Props {
    token: string;
}

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

export default function MessageTemplatesTab({ token }: Props) {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [allGyms, setAllGyms] = useState<Gym[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
    const [managingTemplateName, setManagingTemplateName] = useState<string | null>(null);
    const [selectedGym, setSelectedGym] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchData();
    }, [token]);

    const manageTemplate = templates.find(t => t.templateName === managingTemplateName) || null;

    const fetchData = async (showGlobalLoading = true) => {
        try {
            if (showGlobalLoading) setLoading(true);

            // Fetch both templates and gyms in parallel
            const [templatesRes, gymsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/saas/templates`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_BASE_URL}/api/saas/gyms/list`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (!templatesRes.ok) throw new Error('Failed to fetch templates');
            if (!gymsRes.ok) throw new Error('Failed to fetch gyms');

            const templatesData = await templatesRes.json();
            const gymsData = await gymsRes.json();

            setTemplates(templatesData.data || []);
            setAllGyms(gymsData.data || []);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            if (showGlobalLoading) setLoading(false);
        }
    };

    const handleAddToGym = async (templateName: string) => {
        if (!selectedGym) {
            toast.error('Please select a gym');
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await fetch(`${API_BASE_URL}/api/saas/templates/${templateName}/gyms`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ gymId: selectedGym })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to add template');
            }

            toast.success('Template added to gym successfully');
            setSelectedGym('');
            await fetchData(false);
        } catch (error: any) {
            toast.error(error.message || 'Failed to add template');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveFromGym = async (templateId: string, gymName: string) => {
        if (!confirm(`Remove this template from ${gymName}?`)) return;

        try {
            setIsSubmitting(true);
            const response = await fetch(`${API_BASE_URL}/api/saas/templates/${templateId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('Failed to remove template');

            toast.success('Template removed successfully');
            await fetchData(false);
        } catch (error) {
            toast.error('Failed to remove template');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getAvailableGyms = (template: Template) => {
        const assignedGymIds = template.gyms.map(g => g.gymId);
        return allGyms.filter(gym => !assignedGymIds.includes(gym.id));
    };

    const generateHighlightedContent = (content: string, sampleParams: Record<string, string>) => {
        let highlightedContent = content;
        const colors = ['bg-blue-100', 'bg-green-100', 'bg-yellow-100', 'bg-purple-100', 'bg-pink-100', 'bg-indigo-100'];
        let colorIndex = 0;

        // Handle numeric placeholders {{1}}, {{2}}, etc.
        for (let i = 1; i <= 9; i++) {
            const regex = new RegExp(`\\{\\{${i}\\}\\}`, 'g');
            if (content.match(regex)) {
                const color = colors[colorIndex % colors.length];
                const value = sampleParams[i.toString()] || `Sample ${i}`;
                highlightedContent = highlightedContent.replace(
                    regex,
                    `<span class="${color} px-1 py-0.5 rounded text-gray-800">${value}</span>`
                );
                colorIndex++;
            }
        }

        return highlightedContent;
    };

    const filteredGyms = manageTemplate
        ? manageTemplate.gyms.filter(gym =>
            gym.gymName.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : [];

    if (loading) return <div className="p-4 text-center">Loading templates...</div>;

    return (
        <div className="space-y-4">
            {templates.length === 0 ? (
                <div className="bg-white p-6 rounded-lg border border-gray-200 text-center text-gray-500">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Templates Found</h3>
                    <p>No message templates are currently configured in the system.</p>
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Template
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Trigger Event
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Gyms
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {templates.map((template) => (
                                <tr key={template.templateName} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900">{template.title}</div>
                                        <div className="text-sm text-gray-500">{template.templateName}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                                            {template.type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {template.triggerEvent}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900">
                                                {template.gyms.length}
                                            </span>
                                            <span className="text-sm text-gray-500">
                                                gym{template.gyms.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right text-sm font-medium">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => setPreviewTemplate(template)}
                                                className="text-blue-600 hover:text-blue-900"
                                                title="Preview"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => setManagingTemplateName(template.templateName)}
                                                className="text-indigo-600 hover:text-indigo-900"
                                                title="Manage Gyms"
                                            >
                                                <Settings className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Manage Gyms Modal */}
            {manageTemplate && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">{manageTemplate.title}</h3>
                                    <p className="text-sm text-gray-500 mt-1">Manage gym assignments for this template</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setManagingTemplateName(null);
                                        setSearchQuery('');
                                        setSelectedGym('');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 flex-1 overflow-y-auto">
                            {/* Add Gym Section */}
                            <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Add to New Gym</h4>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={selectedGym}
                                        onChange={(e) => setSelectedGym(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    >
                                        <option value="">Select a gym...</option>
                                        {getAvailableGyms(manageTemplate).map((gym) => (
                                            <option key={gym.id} value={gym.id}>
                                                {gym.displayName || gym.name}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => handleAddToGym(manageTemplate.templateName)}
                                        disabled={!selectedGym || isSubmitting}
                                        className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    >
                                        {isSubmitting ? (
                                            <span className="flex items-center gap-2">
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                Adding...
                                            </span>
                                        ) : (
                                            <>
                                                <Plus className="h-4 w-4" />
                                                Add
                                            </>
                                        )}
                                    </button>
                                </div>
                                {getAvailableGyms(manageTemplate).length === 0 && (
                                    <p className="text-sm text-gray-600 mt-2">This template is assigned to all gyms</p>
                                )}
                            </div>

                            {/* Assigned Gyms List */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-gray-900">
                                        Assigned Gyms ({manageTemplate.gyms.length})
                                    </h4>
                                    {manageTemplate.gyms.length > 5 && (
                                        <input
                                            type="text"
                                            placeholder="Search gyms..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    )}
                                </div>

                                {manageTemplate.gyms.length === 0 ? (
                                    <p className="text-sm text-gray-500 italic text-center py-8">
                                        Not assigned to any gym yet
                                    </p>
                                ) : (
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {filteredGyms.map((gym) => (
                                            <div key={gym.templateId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-2 h-2 rounded-full ${gym.isEnabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                                                    <div>
                                                        <span className="font-medium text-gray-900">{gym.gymName}</span>
                                                        <span className={`ml-2 text-xs px-2 py-0.5 rounded ${gym.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                                            {gym.isEnabled ? 'Enabled' : 'Disabled'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveFromGym(gym.templateId, gym.gymName)}
                                                    disabled={isSubmitting}
                                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Remove from gym"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                        {filteredGyms.length === 0 && searchQuery && (
                                            <p className="text-sm text-gray-500 italic text-center py-4">
                                                No gyms found matching "{searchQuery}"
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setManagingTemplateName(null);
                                    setSearchQuery('');
                                    setSelectedGym('');
                                }}
                                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {previewTemplate && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Template Preview</h3>
                                <button
                                    onClick={() => setPreviewTemplate(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Template Details */}
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">{previewTemplate.title}</h4>
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h5 className="text-sm font-medium text-gray-700 mb-2">Template Content:</h5>
                                        <div className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                                            {previewTemplate.content}
                                        </div>
                                    </div>
                                </div>

                                {/* WhatsApp Preview */}
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">WhatsApp Preview</h4>
                                    <div className="bg-gray-100 rounded-2xl p-4">
                                        <div className="mb-3">
                                            <GympeChatHeader compact />
                                        </div>
                                        <div className="bg-white rounded-lg p-3 shadow-sm">
                                            {previewTemplate.headerType === 'document' && (
                                                <DocumentPreviewCard filename={`Invoice-SAMPLE.pdf`} />
                                            )}
                                            <div
                                                className="text-sm text-gray-800 leading-relaxed"
                                                dangerouslySetInnerHTML={{
                                                    __html: generateHighlightedContent(previewTemplate.content, previewTemplate.sampleParams)
                                                }}
                                            />
                                        </div>
                                        <div className="flex items-center justify-end mt-2">
                                            <div className="flex items-center gap-1">
                                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function X({ className }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}
